import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../../core/services/api.service';
import {
  EvCheckChargerResponse,
  EvDetailByIdPayload,
  EvDetailsResponse,
  EvMemberActionLogRow,
  EvShareVehicleByPlate,
  EvShareVehicleMember,
} from '../../../../../core/models/ev.model';
import { ToastService } from 'angular-toastify';
import { ModalEvChargerCheckStatusComponent } from '../modal-ev-charger-check-status/modal-ev-charger-check-status.component';

@Component({
  selector: 'app-modal-ev-charger-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalEvChargerCheckStatusComponent],
  templateUrl: './modal-ev-charger-detail.component.html',
  styleUrl: './modal-ev-charger-detail.component.css'
})
export class ModalEvChargerDetailComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() data: any = null; // Expecting { historyUuid, licensePlate, siteEv, spaceCode, id }
  @Output() onClose = new EventEmitter<void>();

  isLoading: boolean = false;
  evDetail: EvDetailsResponse | null = null;
  actionLogs: EvMemberActionLogRow[] = [];
  shareInfo: EvShareVehicleByPlate | null = null;
  expandedLogId: number | null = null;
  isCheckingStatus: boolean = false;

  readonly actionLogPageSize = 10;
  actionLogPage = 1;

  // Check Status Modal
  isCheckStatusModalOpen: boolean = false;
  checkStatusData: EvCheckChargerResponse | null = null;

  constructor(
    private apiService: ApiService,
    private toast: ToastService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.data) {
      this.loadEvDetail();
    }
  }

  reloadDetail(): void {
    if (this.isLoading || !this.data?.id) return;
    this.loadEvDetail();
  }

  get activeShareMembers(): EvShareVehicleMember[] {
    return (this.shareInfo?.activeGroup?.members ?? []).filter((m) => !m.disable);
  }

  get actionLogTotalPages(): number {
    return Math.max(1, Math.ceil(this.actionLogs.length / this.actionLogPageSize));
  }

  get pagedActionLogs(): EvMemberActionLogRow[] {
    const start = (this.actionLogPage - 1) * this.actionLogPageSize;
    return this.actionLogs.slice(start, start + this.actionLogPageSize);
  }

  get actionLogStartIndex(): number {
    if (!this.actionLogs.length) return 0;
    return (this.actionLogPage - 1) * this.actionLogPageSize + 1;
  }

  get actionLogEndIndex(): number {
    return Math.min(this.actionLogPage * this.actionLogPageSize, this.actionLogs.length);
  }

  get actionLogPages(): number[] {
    return Array.from({ length: this.actionLogTotalPages }, (_, i) => i + 1);
  }

  get totalChargingKwh(): number {
    return (this.evDetail?.evChargerHistoryResponses ?? []).reduce(
      (sum, row) => sum + (Number(row.chargingKwh) || 0),
      0,
    );
  }

  get totalChargingAmount(): number {
    return (this.evDetail?.evChargerHistoryResponses ?? []).reduce(
      (sum, row) => sum + (Number(row.amount) || 0),
      0,
    );
  }

  setActionLogPage(page: number): void {
    if (page < 1 || page > this.actionLogTotalPages || page === this.actionLogPage) return;
    this.actionLogPage = page;
    this.expandedLogId = null;
  }

  loadEvDetail() {
    if (!this.data?.id) return;

    this.isLoading = true;
    this.evDetail = null;
    this.actionLogs = [];
    this.shareInfo = null;
    this.expandedLogId = null;
    this.actionLogPage = 1;

    const payload: EvDetailByIdPayload = {
      EvId: this.data.id
    };

    const licensePlate = (this.data?.licensePlate || '').trim();

    forkJoin({
      detail: this.apiService.getEvDetailById(payload),
      logs: this.apiService.getEvMemberActionLogsByEvTransactionId(this.data.id).pipe(
        catchError((err) => {
          console.error(err);
          return of([] as EvMemberActionLogRow[]);
        }),
      ),
      share: licensePlate
        ? this.apiService.getShareVehicleByPlate(licensePlate).pipe(
            catchError((err) => {
              console.error(err);
              return of(null);
            }),
          )
        : of(null),
    }).subscribe({
      next: ({ detail, logs, share }) => {
        if (detail?.evChargerHistoryResponses?.length) {
          detail.evChargerHistoryResponses = [...detail.evChargerHistoryResponses].sort(
            (a, b) => this.toTime(b.chargingStart) - this.toTime(a.chargingStart),
          );
        }
        this.evDetail = detail;
        this.actionLogs = Array.isArray(logs) ? logs : [];
        this.shareInfo = share;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading EV details:', err);
        this.toast.error('ไม่สามารถโหลดข้อมูลรายละเอียดได้');
        this.isLoading = false;
      }
    });
  }

  toggleLogDetail(logId: number): void {
    this.expandedLogId = this.expandedLogId === logId ? null : logId;
  }

  actionTypeLabel(actionType: string | null | undefined): string {
    switch ((actionType || '').trim()) {
      case 'start_charge':
        return 'เริ่มชาร์จ';
      case 'stop_charge':
        return 'หยุดชาร์จ';
      case 'qr_manual_entry':
        return 'QR เข้าสถานี';
      case 'qr_plate_correction':
        return 'QR แก้ทะเบียน';
      case 'qr_entry_validation_failed':
        return 'QR ล้มเหลว';
      case 'start_charge_validation_failed':
        return 'เริ่มชาร์จล้มเหลว';
      case 'stop_charge_validation_failed':
        return 'หยุดชาร์จล้มเหลว';
      default:
        return actionType?.trim() || '-';
    }
  }

  resultClass(result: string | null | undefined): string {
    switch ((result || '').trim().toLowerCase()) {
      case 'success':
        return 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'failed':
        return 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300';
      case 'error':
        return 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300';
      default:
        return 'bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300';
    }
  }

  checkStatus() {
    if (!this.data?.id) return;

    this.isCheckingStatus = true;
    const payload: EvDetailByIdPayload = {
      EvId: this.data.id
    };

    this.apiService.checkEvChargerStatus(payload).subscribe({
      next: (res) => {
        this.toast.success('ตรวจสอบสถานะเรียบร้อย');
        this.checkStatusData = res;
        this.isCheckingStatus = false;
        this.isCheckStatusModalOpen = true;
      },
      error: (err) => {
        console.error('Error checking status:', err);
        this.toast.error('ตรวจสอบสถานะล้มเหลว');
        this.isCheckingStatus = false;
      }
    });
  }

  closeCheckStatusModal() {
    this.isCheckStatusModalOpen = false;
    this.checkStatusData = null;
  }

  formatDate(date: string | Date | null): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('th-TH') + ' ' + d.toLocaleTimeString('th-TH');
  }

  /** Newest first; missing dates sink to the bottom. */
  private toTime(value: string | Date | null | undefined): number {
    if (!value) return 0;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? 0 : t;
  }

  closeModal() {
    this.isOpen = false;
    this.onClose.emit();
  }

  getStatusLabel(status: boolean): string {
    switch (status) {
      case false: return 'เสร็จสิ้น';
      case true: return 'กำลังชาร์จ';
      default: return `สถานะ ${status}`;
    }
  }

  getStatusClass(status: boolean): string {
    switch (status) {
      case false: return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
      case true: return 'bg-blue-50 text-blue-700 ring-blue-200';
      default: return 'bg-slate-50 text-slate-700 ring-slate-200';
    }
  }
}
